import { useState, useEffect, useRef } from 'react'
import {
  createAttributeGroups,
  generateExportExcel,
} from './bulkExportUtilities'
import { BULK } from '../../../constants/bulkUtilitiesConstants'
import { BULK_ALERT } from '../../../constants/alertConstants'
import { getBulkExport } from '../../../servicesGraphQl'
import {
  getLovForExport,
  getLovForValidationOptions,
  getItemTypesForReferenceSheet,
} from '../../../services/downloadService'
import { useMain } from '../../main/useMain'
import { useToaster } from '@enterprise-ui/canvas-ui-react'
import { useFirefly } from '../../../utilities/useFirefly'
import { FIREFLY_EVENTS } from '../../../constants/fireFlyConstants'

export const useExport = () => {
  const makeToast = useToaster()
  const { handleTrackClickEvent } = useFirefly() || {}

  //Entire state data
  const defaultSelection = {
    purpose: 'all', // Purpose of export - radiobutton selection
    intendedUse: null, // Intended use
    itemType: null, // Item type
    itemTypeValue: '', // Item type - value
    itemTypeLabel: '', // Item type - label
  }
  const [selectedValues, setSelectedValues] = useState(defaultSelection)

  //Intended Uses options
  const [indendedUses, setIntendedUses] = useState([])
  const [loadingIntendedUse, setLoadingIntendedUse] = useState(false)

  //Item types options
  const [itemTypes, setItemTypes] = useState([])
  const [loadingItemType, setLoadingItemType] = useState(false)

  //Download button state
  const defaultButtonState = {
    inProgress: false,
    disabled: true,
  }
  const [downloadButtonState, setDownloadButtonState] =
    useState(defaultButtonState)

  const [isNoItemsAvailable, setIsNoItemsAvailable] = useState(false)

  const { getUserProfile } = useMain()

  const hasFetchedRef = useRef(false)

  const hasPurposeRef = useRef(false) // To capture only once when the page renders, else it will track every time when ever the page re-renders

  useEffect(() => {
    if (!hasPurposeRef.current && selectedValues?.purpose) {
      handleTrackClickEvent(
        FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.PURPOSE,
        selectedValues.purpose,
      )
      hasPurposeRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) //To track initial purpose selection

  useEffect(() => {
    if (getUserProfile && !hasFetchedRef.current) {
      getIntendedUse()
      hasFetchedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserProfile])

  useEffect(() => {
    const { purpose, intendedUse } = selectedValues ?? {}
    if (purpose === 'readOnly') {
      getItemTypes()
      return
    }
    const value = intendedUse?.value ?? intendedUse
    if (!value) return
    const option = indendedUses.find((o) => o.value === value)

    if (option?.count > 0) {
      getItemTypes()
    } else if (option?.count === 0) {
      setItemTypes([])
      setIsNoItemsAvailable(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues?.intendedUse, selectedValues?.purpose])

  useEffect(() => {
    let isDisabled = true

    if (selectedValues.purpose && selectedValues.itemType) {
      // If we have a purpose and an item type, check the count.
      const selectedItemTypeData = itemTypes.find(
        (it) => it.value === selectedValues.itemType.value,
      )

      // The item count is in the label string, e.g., "Skirts (0 item)"
      // We extract the count from the label.
      const match = selectedItemTypeData?.label.match(/\((\d+)/)
      const count = match ? parseInt(match[1], 10) : 0
      if (count === 0 && selectedValues.purpose === 'all') {
        // Show tooltip if count is 0 and its template with data
        setIsNoItemsAvailable(true)
      } else {
        setIsNoItemsAvailable(false)
      }

      if (count > 0 || selectedValues.purpose === 'readOnly') {
        isDisabled = false // Only enable if the count is greater than 0.
      }
    }
    setDownloadButtonState({
      ...downloadButtonState,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues, itemTypes])

  /**
   * Displays toast for bulk export success, failure or error.
   * @param {string} toastMessage - The message to display in the toast.
   */

  const showToastMessage = (toastMessage, type, heading) => {
    makeToast({
      type: type ? type : 'success',
      heading: heading ? heading : 'Heading',
      message: toastMessage,
    })
  }

  /**
   * Fetches the intended use options.
   * @async
   * @function getItemTypes
   * @returns {Promise<void>} A promise that resolves when the intended use have been fetched and the state has been updated.
   */
  const getIntendedUse = async () => {
    let intendedUses
    try {
      setLoadingIntendedUse(true)
      let intendedUseResponse = await getLovForExport(
        'intendedUse',
        getUserProfile,
        '',
      )

      if (intendedUseResponse?.data) {
        intendedUses = intendedUseResponse?.data
        if (!intendedUses.length) {
          showToastMessage(
            BULK?.BULL_EXPORT_INTENDED_USE_LOAD_FAILED,
            'error',
            'Error',
          )
        }
      }
    } catch {
      showToastMessage(
        BULK?.BULL_EXPORT_INTENDED_USE_LOAD_FAILED,
        'error',
        'Error',
      )
    } finally {
      setLoadingIntendedUse(false)
    }

    if (intendedUses) {
      const intendedUseOptions = intendedUses.map((types) => ({
        value: types.attribute,
        label: `${types.description} (${types.count?.toLocaleString()} ${types.count > 1 ? 'items' : 'item'})`,
        disabled: false,
        count: types.count, // Store count for validation
      }))
      setIntendedUses(intendedUseOptions)
      // Default to VendorNotComplete (Setup), fallback to VendorComplete (Maintain) only if Setup doesn't exist
      const defaultIntendedUse =
        intendedUseOptions.find((opt) => opt.value === 'VendorNotComplete')
          ?.value ||
        intendedUseOptions.find((opt) => opt.value === 'VendorComplete')
          ?.value ||
        null

      setSelectedValues({
        ...selectedValues,
        intendedUse: defaultIntendedUse,
      })
    } else {
      setIntendedUses([])
    }
  }

  /**
   * Fetch item types
   * @async
   * @function getItemTypes
   * @returns {Promise<void>} A promise that resolves when the item types have been fetched and the state has been updated.
   */
  const getItemTypes = async () => {
    let itemTypes
    try {
      setLoadingItemType(true)
      if (selectedValues?.purpose === 'readOnly') {
        const resp = await getItemTypesForReferenceSheet(getUserProfile)
        itemTypes = resp?.data?.data || resp?.data?.items || resp?.data || []
      } else {
        const state =
          typeof selectedValues?.intendedUse === 'object'
            ? selectedValues?.intendedUse?.value
            : selectedValues?.intendedUse || ''
        const itemTypeResponse = await getLovForExport(
          'itemType',
          getUserProfile,
          state,
        )
        itemTypes = itemTypeResponse?.data || []
      }

      if (!Array.isArray(itemTypes) || itemTypes.length === 0) {
        showToastMessage(
          BULK.BULK_EXPORT_ITEM_TYPE_LOAD_FAILED,
          'error',
          'Error',
        )
      }
    } catch {
      showToastMessage(BULK.BULK_EXPORT_ITEM_TYPE_LOAD_FAILED, 'error', 'Error')
    } finally {
      setLoadingItemType(false)
    }

    if (Array.isArray(itemTypes) && itemTypes.length) {
      const itemTypeOptions = itemTypes
        .sort((type1, type2) =>
          (type1?.description || type1?.name || '').localeCompare(
            type2?.description || type2?.name || '',
            undefined,
            { sensitivity: 'base' },
          ),
        )
        .map((types) => {
          const attr =
            types?.attribute ?? types?.id ?? types?.code ?? types?.name ?? ''
          const desc = types?.description ?? types?.name ?? ''
          const baseLabel = desc || attr
          const countNum = Number.isFinite(types?.count) ? types.count : 0

          return {
            id: String(attr),
            value: String(attr),
            label:
              selectedValues.purpose === 'all'
                ? `${String(baseLabel)} (${countNum.toLocaleString()} ${countNum > 1 ? 'items' : 'item'})`
                : String(baseLabel),
          }
        })
      setItemTypes(itemTypeOptions)
    } else {
      setItemTypes([])
    }
  }

  const isItemTypeDisabled = () => {
    const { purpose, intendedUse } = selectedValues || {}
    // For 'readOnly' purpose, enable the dropdown
    if (purpose === 'readOnly') {
      return false
    }
    // For 'all' purpose, check if intended use is selected and has count > 0
    if (purpose === 'all' && intendedUse) {
      const selectedIntendedUseOption = indendedUses.find(
        (option) =>
          option.value === intendedUse || option.value === intendedUse?.value,
      )
      // Disable if count is 0
      if (selectedIntendedUseOption && selectedIntendedUseOption.count === 0) {
        return true
      }
      return false
    }
    return true
  }

  /**
   * Handle "purpose" change - update selected purpose
   * @param {string} newPurpose - The new purpose to set.
   */
  const handlePurposeChange = (newPurpose) => {
    setSelectedValues({ ...selectedValues, purpose: newPurpose })
    //To track purpose selection
    handleTrackClickEvent(
      FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.PURPOSE,
      newPurpose,
    )
  }

  /**
   * Handles "intended use" change - update selected intended use
   * @param {Object} newIntendedUse - The new intended use object.
   * @param {string} newIntendedUse.value - The value of the intended use.
   * @param {string} newIntendedUse.label - The label of the intended use.
   */
  const handleIntendedUseChange = (newIntendedUse) => {
    //NOTE: newIntendedUse has value, label - use the parameter(s) required for BE API
    setSelectedValues({
      ...selectedValues,
      intendedUse: newIntendedUse,
      itemType: null, // Reset item type when intended use changes
    })
    //To track intended use selection
    handleTrackClickEvent(
      FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.INTENDED_USE,
      newIntendedUse,
    )
  }

  /**
   * Handles "item type" change - update selected item type
   * @param {Object} newItemType - The new item type object.
   * @param {string} newItemType.id - The ID of the new item type.
   * @param {string} newItemType.value - The value of the new item type.
   * @param {string} newItemType.label - The label of the new item type.
   */
  const handleItemTypeChange = (newItemType) => {
    setSelectedValues({
      ...selectedValues,
      itemType: newItemType,
      itemTypeValue: newItemType?.value,
      itemTypeLabel: newItemType?.label,
    })
    //To track item type selection
    handleTrackClickEvent(
      FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.ITEM_TYPE,
      JSON.stringify({
        itemTypeId: newItemType?.value, //To track item type id selection
        itemTypeLabel: newItemType?.label.split(' (')[0], //To track item type label without count
      }),
    )
  }

  const handleBulkExport = async () => {
    setDownloadButtonState({ ...downloadButtonState, inProgress: true })
    try {
      const [responseData, validOptionsData] = await Promise.all([
        getBulkExport({
          purpose: selectedValues.purpose,
          itemTypeId: selectedValues.itemTypeValue,
          itemTypeLabel: selectedValues.itemTypeLabel?.split(' (')[0],
          bulkType: 'BULK_UPLOAD',
          vendorCompleteState:
            selectedValues.intendedUse === 'VendorComplete' ? true : false,
        }),
        getLovForValidationOptions(
          getUserProfile,
          selectedValues.itemTypeValue,
        ),
      ])
      const { data } = responseData
      if (data && validOptionsData && validOptionsData.data) {
        const { ExcelExportResponse, errors } = data.getBulkExportData
        if (ExcelExportResponse && ExcelExportResponse?.length) {
          const optionsDataForExcel = createAttributeGroups(
            ExcelExportResponse[0]?.attributeName,
            validOptionsData.data.lov_details,
          )
          const time = Math.floor(Date.now() / 1000)
          const fileName =
            selectedValues?.purpose === 'all'
              ? selectedValues?.intendedUse === 'VendorNotComplete'
                ? `${selectedValues.itemTypeLabel}_Setup_${time}`
                : `${selectedValues.itemTypeLabel}_Maintain_${time}`
              : `${selectedValues.itemTypeLabel}_AttributeReference`
          if (!!ExcelExportResponse && !errors) {
            const downloaded = await generateExportExcel(
              ExcelExportResponse,
              optionsDataForExcel,
              fileName,
              selectedValues.purpose,
              'bulk_export', //type of export - normal or rejected items
            )
            if (downloaded === 'success') {
              showToastMessage(
                BULK_ALERT.BULK_EXPORT_SUCCESS(
                  selectedValues.itemTypeLabel?.split(' (')[0],
                ),
                'success',
                'Spreadsheet Downloaded!',
              )
            } else if (downloaded === 'failed') {
              showToastMessage(errors, 'alert', 'Alert')
            }
            //To track excel download status - success or failed
            handleTrackClickEvent(
              FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS.EXCEL_DOWNLOAD,
              JSON.stringify({
                fileName: fileName,
                downloaded: downloaded,
              }),
            )
          }
        } else {
          showToastMessage(errors, 'error', 'Error')
        }
      } else {
        showToastMessage(BULK_ALERT?.BULK_EXPORT_ERROR, 'alert', 'Alert')
      }
    } catch {
      showToastMessage(BULK_ALERT?.BULK_EXPORT_ERROR, 'error', 'Error')
    }
    setDownloadButtonState({ ...downloadButtonState, inProgress: false })
    setSelectedValues({
      ...defaultSelection,
      intendedUse: 'VendorNotComplete', // Reset intended use to default
    })
  }

  return {
    selectedValues,
    loadingIntendedUse,
    indendedUses,
    loadingItemType,
    itemTypes,
    handlePurposeChange,
    handleIntendedUseChange,
    handleItemTypeChange,
    downloadButtonState,
    handleBulkExport,
    isItemTypeDisabled,
    showToastMessage,
    isNoItemsAvailable,
  }
}
